/*
 * "Is this a gift?" progressive reveal in the Amp Slide Cart drawer.
 *
 * The drawer is the Amp Slide Cart app, not a theme section, so there is no Liquid
 * hook to render into -- Amp builds its own DOM under #slidecarthq at runtime. This
 * injects the section between the line items and the footer, which is where it sits
 * on the reference implementation (directly above Shipping / Subtotal / Checkout).
 *
 * The control itself is <giftnote-button>, the same custom element the PDP uses. The
 * giftnote app embed is enabled store-wide, so the element upgrades anywhere on the
 * page, including inside Amp's DOM. Its internals are in a shadow root; the supported
 * styling hook is a <template> of CSS referenced by data-styles, which is what
 * ensureTemplate() below provides. Everything outside the component is styled from
 * assets/custom.css.
 *
 * Re-injection: in testing the injected node survived cart changes, SLIDECART_UPDATE
 * and close/reopen, but Amp owns this DOM and can rebuild it, so a MutationObserver
 * re-places the section if it goes missing. place() MOVES the existing node rather
 * than rebuilding it, so the giftnote element instance -- and any message the shopper
 * is part-way through -- survives a re-render.
 */
(function () {
  'use strict';

  var STATE_KEY = 'drGiftOpen';
  var TEMPLATE_ID = 'dr-giftnote-styles';
  var PANEL_ID = 'dr-gift-panel';

  var GIFT_ICON =
    '<svg class="dr-gift__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M12 6V22M12 6H8.46A1.96 1.96 0 1 1 8.46 2C11.21 2 12 6 12 6ZM12 6h3.54A1.96 1.96 0 1 0 15.54 2C12.79 2 12 6 12 6ZM20 11v7.8c0 1.12 0 1.68-.22 2.11a2 2 0 0 1-.87.87c-.43.22-.99.22-2.11.22H7.2c-1.12 0-1.68 0-2.11-.22a2 2 0 0 1-.87-.87C4 20.48 4 19.92 4 18.8V11M2 7.6v1.8c0 .56 0 .84.11 1.05a1 1 0 0 0 .44.44c.21.11.49.11 1.05.11h16.8c.56 0 .84 0 1.05-.11a1 1 0 0 0 .44-.44c.11-.21.11-.49.11-1.05V7.6c0-.56 0-.84-.11-1.05a1 1 0 0 0-.44-.44C21.24 6 20.96 6 20.4 6H3.6c-.56 0-.84 0-1.05.11a1 1 0 0 0-.44.44C2 6.76 2 7.04 2 7.6Z" ' +
    'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var CHEVRON =
    '<svg class="dr-gift__chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* Shopper's last choice, so the section does not snap shut on every cart update.
     sessionStorage can throw in private mode, so every access is guarded. */
  function readOpen() {
    try {
      return sessionStorage.getItem(STATE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeOpen(open) {
    try {
      sessionStorage.setItem(STATE_KEY, open ? '1' : '0');
    } catch (e) {
      /* no-op */
    }
  }

  /* giftnote renders <giftnote-button> into a shadow root, so page CSS cannot reach
     its internals. data-styles points at a <template> whose CSS the component adopts;
     #button and #container are the ids it exposes. */
  function ensureTemplate() {
    if (document.getElementById(TEMPLATE_ID)) return;
    var tpl = document.createElement('template');
    tpl.id = TEMPLATE_ID;
    tpl.innerHTML =
      '<style>' +
      '#container{width:100%}' +
      '#button{width:100%;box-sizing:border-box;display:flex;align-items:center;justify-content:center;' +
      'font-family:Raleway,sans-serif;font-size:14px;font-weight:700;letter-spacing:.02em;line-height:1.2;' +
      'color:#0d1a3b;background:#fff;border:1px solid #0d1a3b;border-radius:4px;padding:14px 16px;cursor:pointer}' +
      '#button:hover{background:#0d1a3b;color:#fff}' +
      '</style>';
    document.body.appendChild(tpl);
  }

  function buildSection() {
    var open = readOpen();

    var section = document.createElement('div');
    section.className = 'dr-gift';
    section.setAttribute('data-dr-gift', '');
    section.setAttribute('data-open', open ? 'true' : 'false');

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'dr-gift__toggle';
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute('aria-controls', PANEL_ID);
    toggle.innerHTML =
      '<span class="dr-gift__title">' + GIFT_ICON + '<span>Is this a gift?</span></span>' + CHEVRON;

    var panel = document.createElement('div');
    panel.className = 'dr-gift__panel';
    panel.id = PANEL_ID;
    panel.hidden = !open;
    panel.innerHTML =
      '<giftnote-button data-styles="' + TEMPLATE_ID + '" ' +
      'data-add="add a free gift message" data-edit="edit gift message"></giftnote-button>';

    toggle.addEventListener('click', function () {
      var nowOpen = section.getAttribute('data-open') !== 'true';
      section.setAttribute('data-open', nowOpen ? 'true' : 'false');
      toggle.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
      panel.hidden = !nowOpen;
      writeOpen(nowOpen);
    });

    /* The giftnote composer renders full-screen at the maximum z-index, but so does
       Amp's drawer, and the drawer comes later in the document -- so the composer
       opens *behind* it and the shopper sees nothing. Close the drawer as the
       composer opens, and bring the drawer back when the composer closes. */
    /* giftnote's own button stops the click before it bubbles, so listen on the
       document in the capture phase instead. */
    if (!window.__drGiftComposerHook) {
      window.__drGiftComposerHook = true;
      document.addEventListener('click', function (event) {
        var path = event.composedPath ? event.composedPath() : [event.target];
        var fromDrawer = path.some(function (node) {
          return node && node.classList && node.classList.contains('dr-gift__panel');
        });
        if (!fromDrawer) return;
        waitForComposer(function () {
          if (typeof window.SLIDECART_CLOSE === 'function') window.SLIDECART_CLOSE();
        });
      }, true);
    }

    section.appendChild(toggle);
    section.appendChild(panel);
    return section;
  }

  /* Poll briefly for the composer to appear, then watch for it closing. */
  function waitForComposer(onOpen) {
    var started = Date.now();
    (function check() {
      var core = document.querySelector('giftnote-core');
      var modal = core && core.shadowRoot && core.shadowRoot.querySelector('div');
      var visible = modal && getComputedStyle(modal).display !== 'none';
      if (visible) {
        onOpen();
        watchForClose(modal);
      } else if (Date.now() - started < 4000) {
        setTimeout(check, 100);
      }
    })();
  }

  function watchForClose(modal) {
    var observer = new MutationObserver(function () {
      if (getComputedStyle(modal).display === 'none') {
        observer.disconnect();
        if (typeof window.SLIDECART_OPEN === 'function') window.SLIDECART_OPEN();
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['style', 'class'] });
    /* Fallback: the app may swap the node rather than hide it. */
    var started = Date.now();
    (function poll() {
      if (!modal.isConnected || getComputedStyle(modal).display === 'none') {
        observer.disconnect();
        if (typeof window.SLIDECART_OPEN === 'function') window.SLIDECART_OPEN();
      } else if (Date.now() - started < 300000) {
        setTimeout(poll, 500);
      }
    })();
  }

  /* Insert before Amp's footer, or move an existing section back there if Amp has
     rebuilt around it. Returns true once the section is in the right place. */
  function place() {
    var drawer = document.querySelector('#slidecarthq .slidecarthq');
    if (!drawer) return false;

    var existing = drawer.querySelector('[data-dr-gift]');

    /* No footer means an empty cart. Amp swaps the footer out but leaves everything
       else standing, so an already-injected section would sit above "Your cart is
       empty" offering to gift-wrap nothing. Take it back out. */
    var footer = drawer.querySelector('footer.footer');
    if (!footer) {
      if (existing) existing.parentNode.removeChild(existing);
      return false;
    }

    if (existing) {
      if (existing.nextElementSibling !== footer) {
        footer.parentNode.insertBefore(existing, footer);
      }
      return true;
    }

    ensureTemplate();
    footer.parentNode.insertBefore(buildSection(), footer);
    return true;
  }

  var pending = null;
  function schedule() {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      place();
    }, 60);
  }

  function start() {
    place();

    /* Observe document.body rather than #slidecarthq: Amp owns that root and can
       replace it, which would leave us watching a detached node with no way back.
       body always outlives it. place() bails on its first querySelector when the
       drawer is absent, and the 60ms debounce bounds how often that runs. */
    try {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    } catch (e) {
      /* observer unavailable: the load-time and API-hook passes still cover the common paths */
    }

    /* Amp re-renders behind these, so re-place straight after they run. */
    ['SLIDECART_OPEN', 'SLIDECART_UPDATE'].forEach(function (name) {
      var original = window[name];
      if (typeof original !== 'function') return;
      window[name] = function () {
        var result = original.apply(this, arguments);
        schedule();
        return result;
      };
    });
  }

  /* Guard against this file being evaluated twice (duplicated script tag, app that
     re-injects assets): a second run would stack another observer and another layer
     of SLIDECART wrappers. */
  function boot() {
    if (window.__drGiftNoteBooted) return;
    window.__drGiftNoteBooted = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }

  /* Wait for <giftnote-button> to be defined before injecting anything. If the
     giftnote app fails to load or is removed, the element never upgrades and the
     panel would open onto blank padding -- better that the section never appears.
     The app embed is enabled store-wide, so in the normal case this resolves almost
     immediately. */
  if (window.customElements && typeof customElements.whenDefined === 'function') {
    customElements.whenDefined('giftnote-button').then(boot);
  } else {
    boot();
  }
})();
